import { RunAgentModal } from '@/components/AgentModals';
import { CenterSpin } from '@/components/CenterSpin';
import { DetailsPageLayout } from '@/layouts/DetailsPageLayout';
import { useAgentConfigurationVersionsByAgentId } from '@/queries/agent-configuration-versions';
import { useAgentExecutionsByAgentId } from '@/queries/agent-executions';
import type { AgentExecution } from '@/queries/agent-executions';
import { useAgentById, useDeleteAgent, useUpdateAgent } from '@/queries/agents';
import { formatRelativeTime, getErrorMessage } from '@/utils';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import ExperimentOutlined from '@ant-design/icons/ExperimentOutlined';
import HistoryOutlined from '@ant-design/icons/HistoryOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import ShareAltOutlined from '@ant-design/icons/ShareAltOutlined';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Table from 'antd/es/table';
import type { ColumnsType } from 'antd/es/table';
import Tag from 'antd/es/tag';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DetailRow, VersionHistoryButton } from 'ui-components';

const VersionBadge = styled.span`
  ${({ theme }) => `
  display: inline-flex;
  align-items: center;
  gap: ${theme.marginXXS}px;
  padding: ${theme.paddingXXS / 2}px ${theme.paddingXS}px;
  border-radius: ${theme.borderRadiusSM}px;
  font-size: ${theme.fontSizeSM}px;
  font-weight: ${theme.fontWeightStrong};
  background: ${theme.colorSuccessBg};
  color: ${theme.colorSuccess};

  .anticon {
    font-size: ${theme.fontSizeSM}px;
  }
`}
`;

const EmptyStateIcon = styled.div`
  ${({ theme }) => `
  font-size: ${theme.fontSizeLG * 3}px;
  margin-bottom: ${theme.margin}px;
  opacity: ${theme.opacityImage * 0.3};
`}
`;

const EditActionsContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.marginXS}px;
  align-items: center;
  flex-shrink: 0;
  margin-top: ${({ theme }) => theme.marginSM}px;

  .ant-btn {
    min-height: ${({ theme }) => theme.controlHeight}px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.ant-btn-sm {
      min-height: ${({ theme }) => theme.controlHeightSM}px;
      padding: ${({ theme }) => theme.paddingXXS}px
        ${({ theme }) => theme.paddingXS}px;
    }
  }
`;

const SectionContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.marginLG}px;
`;

const UppercaseLabel = styled(Typography.Text)`
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colorTextSecondary};
`;

const DescriptionContainer = styled.div`
  margin-top: ${({ theme }) => theme.marginXS}px;
`;

const EmptyStateSection = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.paddingXL}px 0;
`;

const EmptyStateTitle = styled(Typography.Title)`
  margin: 0 0 ${({ theme }) => theme.marginXS}px 0 !important;
`;

const EmptyStateDescription = styled(Typography.Text)`
  display: block;
  margin-bottom: ${({ theme }) => theme.margin}px;
`;

const ExecutionStatusTag: React.FC<{ status: string }> = ({ status }) => {
  const colorMap: Record<string, string> = {
    pending: 'default',
    running: 'processing',
    completed: 'success',
    failed: 'error',
    cancelled: 'warning',
    paused: 'warning',
  };

  return <Tag color={colorMap[status] || 'default'}>{status}</Tag>;
};

export const AgentDetailPage: React.FC = () => {
  const { teamId, agentId } = useParams<{
    teamId: string;
    agentId: string;
  }>();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const {
    data: agent,
    isLoading,
    error,
  } = useAgentById(agentId || null, {
    enabled: !!agentId,
  });

  const updateAgentMutation = useUpdateAgent();
  const deleteAgentMutation = useDeleteAgent();

  // Fetch configuration versions
  const { data: configVersions, isLoading: isLoadingVersions } =
    useAgentConfigurationVersionsByAgentId(agentId || null, {
      enabled: !!agentId,
    });

  // Fetch executions
  const { data: executions, isLoading: isLoadingExecutions } =
    useAgentExecutionsByAgentId(agentId || null);

  // Get current configuration version
  const currentVersion = configVersions?.find((v) => v.isCurrent);

  // Calculate stats
  const executionCount = executions?.items.length || 0;
  const successCount =
    executions?.items.filter((e) => e.status === 'completed').length || 0;
  const successRate =
    executionCount > 0 ? Math.round((successCount / executionCount) * 100) : 0;
  const lastExecution = executions?.items[0];

  // Edit states
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');

  // Modal states
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);

  // Edit handlers
  const startEditingDescription = () => {
    if (agent) {
      setEditedDescription(agent.description || '');
      setIsEditingDescription(true);
    }
  };

  const cancelEditingDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription('');
  };

  const saveDescriptionEdit = async () => {
    if (!agent || !agentId) return;

    try {
      await updateAgentMutation.mutateAsync({
        agentId,
        data: { description: editedDescription.trim() || undefined },
      });
      setIsEditingDescription(false);
      void message.success('Agent description updated successfully');
    } catch (error) {
      const errorMessage = getErrorMessage(
        error,
        'Failed to update agent description',
      );
      void message.error(errorMessage);
    }
  };

  const handleDeleteAgent = () => {
    if (!agent) return;

    void modal.confirm({
      title: 'Delete Agent',
      content: `Are you sure you want to delete "${agent.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteAgentMutation.mutateAsync(agent.agentId);
          void message.success('Agent deleted successfully');
          void navigate(`/team/${teamId}/agents`);
        } catch (error) {
          const errorMessage = getErrorMessage(error, 'Failed to delete agent');
          void message.error(errorMessage);
        }
      },
    });
  };

  // Copy the agent id to the clipboard
  const handleCopyId = async () => {
    if (!agent) return;

    try {
      await navigator.clipboard.writeText(agent.agentId);
      void message.success('ID copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy ID');
    }
  };

  const handleShareAgent = useCallback(async () => {
    try {
      const currentUrl = window.location.href;
      await navigator.clipboard.writeText(currentUrl);
      void message.success('Agent URL copied to clipboard');
    } catch (error) {
      void message.error('Failed to copy URL to clipboard');
    }
  }, [message]);

  const handleConfigureAgent = useCallback(() => {
    // TODO: Implement configure agent functionality
    void message.info('Configuration editor will be implemented soon');
  }, [message]);

  const executionColumns: ColumnsType<AgentExecution> = useMemo(
    () => [
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: string) => <ExecutionStatusTag status={status} />,
      },
      {
        title: 'Input Summary',
        dataIndex: 'agentInputSummary',
        key: 'agentInputSummary',
        ellipsis: true,
        render: (summary: string) => summary || '-',
      },
      {
        title: 'Started',
        dataIndex: 'startedAt',
        key: 'startedAt',
        width: 150,
        render: (date: Date | null) =>
          date ? formatRelativeTime(date) : 'Not started',
      },
      {
        title: 'Duration',
        key: 'duration',
        width: 120,
        render: (_: unknown, record: AgentExecution) => {
          if (!record.startedAt) return '-';
          const endTime = record.completedAt || record.failedAt || new Date();
          const duration =
            new Date(endTime).getTime() - new Date(record.startedAt).getTime();
          const seconds = Math.floor(duration / 1000);
          const minutes = Math.floor(seconds / 60);
          if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`;
          }
          return `${seconds}s`;
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        render: (_: unknown, record: AgentExecution) => (
          <Button
            type="link"
            size="small"
            onClick={() => {
              void navigate(
                `/team/${teamId}/agents/${agentId}/executions/${record.agentExecutionId}`,
              );
            }}
          >
            View
          </Button>
        ),
      },
    ],
    [navigate, teamId, agentId],
  );

  // Determine layout props based on state
  const getLayoutProps = () => {
    if (!teamId || !agentId) {
      return {
        title: 'Missing Information',
        breadcrumbs: [
          {
            label: 'Agents',
            path: teamId ? `/team/${teamId}/agents` : '/',
          },
          { label: 'Error' },
        ],
      };
    }

    if (isLoading || isLoadingVersions || isLoadingExecutions) {
      return {
        title: 'Loading Agent',
        breadcrumbs: [
          { label: 'Agents', path: `/team/${teamId}/agents` },
          { label: agent?.name || 'Loading...' },
        ],
      };
    }

    if (error) {
      return {
        title: 'Error Loading Agent',
        breadcrumbs: [
          { label: 'Agents', path: `/team/${teamId}/agents` },
          { label: 'Error' },
        ],
      };
    }

    if (!agent) {
      return {
        title: 'Agent Not Found',
        breadcrumbs: [
          { label: 'Agents', path: `/team/${teamId}/agents` },
          { label: 'Not Found' },
        ],
      };
    }

    return {
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {agent.name}
          <Tag color="green">Active</Tag>
        </div>
      ),
      breadcrumbs: [
        { label: 'Agents', path: `/team/${teamId}/agents` },
        { label: agent.name },
      ],
      actions: [
        {
          label: 'Run Agent',
          type: 'primary' as const,
          icon: <PlayCircleOutlined />,
          onClick: () => setIsRunModalOpen(true),
        },
        {
          label: 'Workbench',
          type: 'secondary' as const,
          icon: <ExperimentOutlined />,
          onClick: () => {
            void navigate(`/team/${teamId}/agents/${agentId}/workbench`);
          },
        },
        {
          label: 'Configure',
          type: 'primary' as const,
          icon: <SettingOutlined />,
          onClick: handleConfigureAgent,
        },
        {
          label: 'Share agent',
          type: 'primary' as const,
          icon: <ShareAltOutlined />,
          onClick: handleShareAgent,
        },
        {
          label: 'Delete agent',
          type: 'primary' as const,
          icon: <DeleteOutlined />,
          danger: true,
          divided: true,
          onClick: handleDeleteAgent,
        },
      ],
    };
  };

  const layoutProps = getLayoutProps();

  // Render different content based on state
  const renderContent = () => {
    if (!teamId || !agentId) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="danger">
              Unable to load agent details. Team or agent information is
              missing.
            </Typography.Text>
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                onClick={() => {
                  window.location.href = teamId
                    ? `/team/${teamId}/agents`
                    : '/';
                }}
              >
                Back to Agents
              </Button>
            </div>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (isLoading || isLoadingVersions || isLoadingExecutions) {
      return (
        <DetailsPageLayout.TopContent>
          <CenterSpin />
        </DetailsPageLayout.TopContent>
      );
    }

    if (error) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="danger">
              There was an error loading the agent details. Please try again.
            </Typography.Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    if (!agent) {
      return (
        <DetailsPageLayout.TopContent>
          <Card>
            <Typography.Text type="secondary">
              The agent you're looking for could not be found.
            </Typography.Text>
          </Card>
        </DetailsPageLayout.TopContent>
      );
    }

    // Render success state with side content and main content
    return [
      <DetailsPageLayout.SideContent key="side-content" title="Agent Overview">
        <SectionContainer>
          <UppercaseLabel type="secondary">ABOUT THIS AGENT</UppercaseLabel>
          <DescriptionContainer>
            {isEditingDescription ? (
              <div>
                <Input.TextArea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void saveDescriptionEdit();
                    }
                  }}
                  placeholder="Enter a description for this agent..."
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  autoFocus
                />
                <EditActionsContainer>
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={saveDescriptionEdit}
                    loading={updateAgentMutation.isPending}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={cancelEditingDescription}
                  >
                    Cancel
                  </Button>
                </EditActionsContainer>
              </div>
            ) : (
              <div
                style={{ cursor: 'pointer' }}
                onClick={startEditingDescription}
              >
                <Typography.Text style={{ lineHeight: '1.5' }}>
                  {agent.description ? (
                    agent.description
                  ) : (
                    <Typography.Text type="secondary" italic>
                      No description provided - click to add one
                    </Typography.Text>
                  )}
                </Typography.Text>
              </div>
            )}
          </DescriptionContainer>
        </SectionContainer>

        <DetailRow>
          <Typography.Text>Agent ID</Typography.Text>
          <Tooltip title="Click to copy ID">
            <Typography.Text
              code
              style={{ cursor: 'pointer' }}
              onClick={handleCopyId}
            >
              {agent.agentId}
            </Typography.Text>
          </Tooltip>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Last Executed</Typography.Text>
          <Typography.Text>
            {lastExecution
              ? formatRelativeTime(
                  lastExecution.startedAt || lastExecution.createdAt,
                )
              : 'Never'}
          </Typography.Text>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Total Executions</Typography.Text>
          <Typography.Text>{executionCount}</Typography.Text>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Success Rate</Typography.Text>
          <Typography.Text>
            {executionCount > 0 ? `${successRate}%` : '-'}
          </Typography.Text>
        </DetailRow>
        <DetailRow>
          <Typography.Text>Created</Typography.Text>
          <Typography.Text>
            {formatRelativeTime(agent.createdAt)}
          </Typography.Text>
        </DetailRow>
      </DetailsPageLayout.SideContent>,

      <DetailsPageLayout.MainContent
        key="main-content-configuration"
        title="Current Configuration"
        extra={
          currentVersion && (
            <VersionHistoryButton
              icon={<HistoryOutlined />}
              onClick={() => {
                void message.info(
                  'Version history modal will be implemented soon',
                );
              }}
            >
              Version History
            </VersionHistoryButton>
          )
        }
      >
        {currentVersion ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <VersionBadge>
                <CheckCircleOutlined />
                Version {currentVersion.versionNumber}
              </VersionBadge>
              <Typography.Text
                type="secondary"
                style={{ marginLeft: 8, fontSize: 12 }}
              >
                Updated {formatRelativeTime(currentVersion.updatedAt)}
              </Typography.Text>
            </div>

            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <UppercaseLabel type="secondary">INSTRUCTIONS</UppercaseLabel>
                <div style={{ marginTop: 4 }}>
                  <Typography.Text
                    style={{
                      display: 'block',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.5,
                    }}
                    ellipsis={{ tooltip: true }}
                  >
                    {currentVersion.instructions}
                  </Typography.Text>
                </div>
              </div>

              <div>
                <UppercaseLabel type="secondary">TOOLS</UppercaseLabel>
                <div style={{ marginTop: 4 }}>
                  <Typography.Text>
                    {currentVersion.toolConfig.length} tools configured
                  </Typography.Text>
                </div>
              </div>

              <div>
                <UppercaseLabel type="secondary">
                  EXECUTION LIMITS
                </UppercaseLabel>
                <div style={{ marginTop: 4 }}>
                  <Typography.Text>
                    Max {currentVersion.executionConfig.max_actions} actions,{' '}
                    {currentVersion.executionConfig.timeout_minutes} min timeout
                  </Typography.Text>
                </div>
              </div>
            </Space>

            <div style={{ marginTop: 24 }}>
              <Button onClick={handleConfigureAgent}>Edit Configuration</Button>
            </div>
          </div>
        ) : (
          <EmptyStateSection>
            <EmptyStateIcon>
              <SettingOutlined />
            </EmptyStateIcon>
            <EmptyStateTitle level={5}>No configuration yet</EmptyStateTitle>
            <EmptyStateDescription type="secondary">
              Configure your agent to define its behavior and capabilities
            </EmptyStateDescription>
            <Button
              type="primary"
              icon={<SettingOutlined />}
              onClick={handleConfigureAgent}
            >
              Configure Agent
            </Button>
          </EmptyStateSection>
        )}
      </DetailsPageLayout.MainContent>,

      <DetailsPageLayout.MainContent
        key="main-content-executions"
        title="Recent Executions"
        extra={
          executions &&
          executions.items.length > 0 && (
            <Link to={`/team/${teamId}/agents/${agentId}/executions`}>
              <Button type="link" size="small">
                View All
              </Button>
            </Link>
          )
        }
      >
        {executions && executions.items.length > 0 ? (
          <Table
            columns={executionColumns}
            dataSource={executions.items.slice(0, 5)}
            rowKey="agentExecutionId"
            pagination={false}
            size="small"
          />
        ) : (
          <EmptyStateSection>
            <EmptyStateIcon>
              <RobotOutlined />
            </EmptyStateIcon>
            <EmptyStateTitle level={5}>No executions yet</EmptyStateTitle>
            <EmptyStateDescription type="secondary">
              Run this agent to see execution history and results
            </EmptyStateDescription>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => setIsRunModalOpen(true)}
            >
              Run Agent
            </Button>
          </EmptyStateSection>
        )}
      </DetailsPageLayout.MainContent>,
    ];
  };

  return (
    <div>
      <DetailsPageLayout {...layoutProps}>{renderContent()}</DetailsPageLayout>

      {agentId && currentVersion && (
        <RunAgentModal
          open={isRunModalOpen}
          onClose={() => setIsRunModalOpen(false)}
          agentId={agentId}
          configurationVersion={currentVersion}
          teamId={teamId}
        />
      )}
    </div>
  );
};
